
import React, { useState, useEffect, useMemo } from "react";
import { GalleryItem } from "./gallery/GalleryItem";
import { useParams } from "react-router-dom";
import { GalleryImage } from "@/types/memorial";
import { PlayCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Media } from "./media/types";
import { LazyImage } from "./common/LazyImage";
import { useOptimizedMemorialData } from "@/hooks/useOptimizedMemorialData";

interface GalleryGridProps {
  onViewMedia?: (url: string, index: number) => void;
}

export const GalleryGrid = ({ onViewMedia }: GalleryGridProps) => {
  const { id } = useParams<{ id: string }>();
  const isMobile = useIsMobile();
  const [isSafari, setIsSafari] = useState(false);

  const { data: memorialData, isLoading } = useOptimizedMemorialData(id);
  
  useEffect(() => {
    // Detect Safari browser
    const userAgent = navigator.userAgent;
    const isSafariCheck = /^((?!chrome|android).)*safari/i.test(userAgent) || 
                        (userAgent.includes('AppleWebKit') && !userAgent.includes('Chrome'));
    
    setIsSafari(isSafariCheck);
  }, []);

  // Memoize the images transformation to avoid unnecessary re-renders
  const images = useMemo(() => {
    if (!memorialData?.media) return [];
    
    return (memorialData.media as Media[]).map((item, index) => ({
      id: item.id,
      src: item.url,
      alt: item.caption || `Gallery image ${index + 1}`,
      caption: item.caption,
      thumbnail: item.thumbnail_url,
      stream_id: item.stream_id
    }));
  }, [memorialData?.media]);

  const isVideo = (image: { src: string; stream_id?: string }) => {
    return image.stream_id ||
           image.src.toLowerCase().endsWith('.mp4') ||
           image.src.toLowerCase().endsWith('.mov') ||
           image.src.toLowerCase().endsWith('.avi');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  // If onViewMedia is provided, use our custom viewer, otherwise use the carousel
  if (onViewMedia) {
    return (
      <div>
        {images.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 sm:gap-2">
            {images.map((image, index) => (
              <div 
                key={image.id} 
                className="aspect-square cursor-pointer hover:opacity-90 transition-opacity relative"
                onClick={() => {
                  onViewMedia(image.src, index);
                }}
              >
                 {isVideo(image) ? (
                   <div className="w-full h-full bg-gray-800 flex items-center justify-center relative">
                     <div className="absolute inset-0 flex items-center justify-center z-10">
                       <PlayCircle className={`${isMobile ? "w-12 h-12" : "w-16 h-16"} text-white opacity-90`} />
                     </div>
                     <LazyImage
                       src={image.thumbnail || `/video-thumbnail-${(Math.abs(index % 3) + 1)}.jpg`}
                       alt="Video thumbnail"
                       className="w-full h-full object-cover opacity-70"
                       placeholder={`/video-thumbnail-${(Math.abs(index % 3) + 1)}.jpg`}
                     />
                   </div>
                 ) : (
                   <LazyImage
                     src={image.src}
                     alt={image.alt}
                     className="w-full h-full object-cover"
                     thumbnailSrc={image.thumbnail}
                   />
                 )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No gallery images available</p>
          </div>
        )}
      </div>
    );
  }

  // Use the original carousel-based gallery
  return (
    <div>
      {images.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 sm:gap-2">
          {images.map((image) => (
            <GalleryItem 
              key={image.id}
              image={image}
              allImages={images}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">No gallery images available</p>
        </div>
      )}
    </div>
  );
};
